import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleNotch, MagnifyingGlass, CheckCircle, WarningCircle, Info } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/dates";

function itemLink(checkKey, item) {
  if (checkKey.startsWith("opportunities")) return `/opportunities/${item.id}`;
  if (checkKey === "prospects_unassigned") return `/prospects/${item.id}`;
  if (checkKey === "activities_missing_date" && item.prospect_id) return `/prospects/${item.prospect_id}`;
  return null;
}

function CheckCard({ check }) {
  const [open, setOpen] = useState(false);
  const clean = check.count === 0;
  const actionRequired = check.severity === "action_required";
  return (
    <div className={`rounded-sm border bg-white ${clean ? "border-[#E4E4E7]" : actionRequired ? "border-[#B45309]/50" : "border-[#3B82F6]/40"}`}
      data-testid={`verification-check-${check.key}`}>
      <button onClick={() => !clean && setOpen((o) => !o)} disabled={clean}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="flex min-w-0 items-center gap-2.5">
          {clean
            ? <CheckCircle size={20} weight="fill" className="shrink-0 text-[#569A1F]" />
            : actionRequired
              ? <WarningCircle size={20} weight="fill" className="shrink-0 text-[#B45309]" />
              : <Info size={20} weight="fill" className="shrink-0 text-[#3B82F6]" />}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#09090B]">{check.label}</p>
            <p className="text-xs text-[#6D6D70]">{check.explanation}</p>
          </div>
        </div>
        <span className={`shrink-0 rounded-sm px-2 py-0.5 font-heading text-sm font-black ${clean ? "bg-[#569A1F]/10 text-[#457d18]" : actionRequired ? "bg-[#F59E0B]/15 text-[#B45309]" : "bg-[#3B82F6]/10 text-[#1D4ED8]"}`}
          data-testid={`verification-count-${check.key}`}>
          {check.count}
        </span>
      </button>
      {open && !clean && (
        <div className="border-t border-[#F4F4F5] px-4 py-2" data-testid={`verification-items-${check.key}`}>
          {check.items.map((item) => {
            const to = itemLink(check.key, item);
            const label = item.name || item.organization_name || item.subject || item.id;
            const owner = item.owner_name ? ` · ${item.owner_name}` : "";
            return to ? (
              <Link key={item.id} to={to} className="block truncate py-1 text-xs font-medium text-[#032852] hover:text-[#569A1F]">
                {label}{owner} →
              </Link>
            ) : (
              <p key={item.id} className="truncate py-1 text-xs text-[#52525B]">{label}{owner}</p>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Read-only legacy-data audit. Nothing is modified or deleted — findings are
// corrected deliberately from their own screens.
export function DataVerificationTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/data-verification");
      setReport(data);
    } catch (e) {
      toast.error("Verification report could not be generated.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="data-verification-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-[#E4E4E7] bg-white p-4">
        <div>
          <p className="text-sm font-semibold text-[#09090B]">Legacy-data verification</p>
          <p className="text-xs text-[#6D6D70]">
            Scans historical records for missing properties, origins, dates and ownership.
            The scan is read-only — no data is ever changed or deleted.
          </p>
        </div>
        <Button onClick={run} disabled={loading} className="rounded-sm bg-[#032852] font-semibold text-white hover:bg-[#021d3d]" data-testid="run-verification-btn">
          {loading ? <CircleNotch size={16} className="animate-spin" /> : <><MagnifyingGlass size={15} /> Run verification</>}
        </Button>
      </div>
      {report && (
        <div className="mt-4 space-y-2" data-testid="verification-report">
          <p className="text-xs text-[#6D6D70]" data-testid="verification-summary">
            Generated {formatDateTime(report.generated_at)} — <span className="font-bold text-[#032852]">{report.total_findings}</span> finding(s) across {report.checks.length} checks.
          </p>
          {report.checks.map((c) => <CheckCard key={c.key} check={c} />)}
        </div>
      )}
    </div>
  );
}
